import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Download,
  FileText,
  LockKeyhole,
  Plus,
  ReceiptText,
  Sparkles,
  WalletCards,
  X,
} from 'lucide-react';

const BRAND = '#375AE7';
const PAGE_SIZE = 8;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const CARDS_PATH = '/api/v1/agency/subscription/cards';
const MICROFORM_SESSION_PATH = '/api/v1/agency/subscription/microform-session';

const emptyManualCard = {
  cardNumber: '',
  expiry: '',
  cvv: '',
};

const formatCardNumber = (value) =>
  value
    .replace(/\D/g, '')
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  return digits.length > 2
    ? `${digits.slice(0, 2)}/${digits.slice(2)}`
    : digits;
};

const defaultCardId = (cards) =>
  cards.find((card) => card.is_default)?.id || cards[0]?.id || '';

const cardBrandLabel = (brand) => {
  const normalized = String(brand || 'card').toUpperCase();
  return normalized === 'AMERICAN_EXPRESS' ? 'AMEX' : normalized;
};

const defaultFormatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const defaultFormatCents = (value, currency = 'AUD') => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
  }).format(Number(value) / 100);
};

const statusStyles = {
  paid: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  settled: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  issued: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  overdue: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  failed: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  draft: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
};

const transactionStatusStyles = {
  completed: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-emerald-50 text-emerald-700',
  succeeded: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
  refunded: 'bg-violet-50 text-violet-700',
  failed: 'bg-red-50 text-red-700',
};

function statusClass(status, styles) {
  return (
    styles[String(status || '').toLowerCase()] ||
    'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
  );
}

/* Microform field container — visually identical to paymentInputClass,
   focus ring is driven by the field's focus/blur events. */
const microFieldClass = (focused, error, extra = '') =>
  `relative flex h-12 w-full items-center overflow-hidden rounded-xl border bg-white px-3.5 text-sm font-medium text-slate-900 transition ${
    error
      ? 'border-red-300 ring-2 ring-red-100'
      : focused
        ? 'border-[#375AE7] ring-4 ring-[#375AE7]/10'
        : 'border-slate-200 hover:border-slate-300'
  } ${extra}`;

function StatCard({ label, value, caption, icon: Icon, tone = 'blue' }) {
  const tones = {
    blue: 'bg-[#EEF2FF] text-[#375AE7]',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_12px_32px_-26px_rgba(15,23,42,0.4)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-24px_rgba(55,90,231,0.3)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            {label}
          </p>
          <p className="mt-2 text-xl font-bold tracking-[-0.03em] text-slate-900">
            {value}
          </p>
          {caption ? (
            <p className="mt-1 text-xs text-slate-400">{caption}</p>
          ) : null}
        </div>
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${tones[tone]}`}
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function EmptyTransactions() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 text-slate-400">
        <WalletCards className="h-5 w-5" />
      </span>
      <h3 className="mt-4 text-sm font-semibold text-slate-800">
        No transactions yet
      </h3>
      <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500">
        Transactions connected to this invoice will appear here once payment
        activity is recorded.
      </p>
    </div>
  );
}

const paymentInputClass =
  'h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-[#375AE7] focus:ring-4 focus:ring-[#375AE7]/10';

function PaymentField({ id, label, icon: Icon, className = '', children }) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500"
      >
        {label}
      </label>
      <div className="relative">
        {Icon ? (
          <Icon className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
        ) : null}
        {children}
      </div>
    </div>
  );
}

export default function InvoiceDetailScreen({
  invoiceDetail: providedInvoice,
  detailLoading = false,
  transactions: providedTransactions,
  cards: providedCards,
  baseUrl = BASE_URL,
  showToast,
  onPay,
  onDownloadPdf,
  formatDate = defaultFormatDate,
  formatCents = defaultFormatCents,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams();
  /* The route param name varies between routers (invoiceId / invoice_id /
     id) — and if the screen is rendered from a parent route without a
     named param, fall back to the numeric segment of the path
     (/invoices/2 → "2"). */
  const routeInvoiceId = useMemo(() => {
    const fromParams =
      routeParams.invoiceId ?? routeParams.invoice_id ?? routeParams.id ?? null;
    if (fromParams !== null && fromParams !== '') return String(fromParams);
    const segments = String(location.pathname || '')
      .split('/')
      .filter(Boolean);
    const numericSegments = segments.filter((segment) => /^\d+$/.test(segment));
    return numericSegments.length
      ? numericSegments[numericSegments.length - 1]
      : null;
  }, [routeParams, location.pathname]);
  const [page, setPage] = useState(1);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [fetchedCards, setFetchedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('saved');
  const [selectedCardId, setSelectedCardId] = useState('');
  const [manualCard, setManualCard] = useState(emptyManualCard);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  /* Self-fetch state — used only when the page is opened directly
     (refresh / deep link) without navigation state. */
  const [fetchedInvoice, setFetchedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [fetchError, setFetchError] = useState('');

  /* ----------------------- Microform (secure fields) ------------------- */
  const [microformData, setMicroformData] = useState(null); // { jwt }
  const [flexReady, setFlexReady] = useState(
    typeof window !== 'undefined' && !!window.Flex,
  );
  const [microformReady, setMicroformReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const [cardSuccess, setCardSuccess] = useState(false);
  const [cvvError, setCvvError] = useState('');
  const [cvvSuccess, setCvvSuccess] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const [cvvFocused, setCvvFocused] = useState(false);
  const microformRef = useRef(null); // { microform, number, securityCode }

  // The list screen can pass the invoice through navigation state. A parent
  // can also provide the fully loaded invoice via the invoiceDetail prop.
  // When neither exists (direct open / refresh) we fetch it from the route
  // param below.
  const invoiceDetail =
    providedInvoice || location.state?.invoice || fetchedInvoice || null;
  const currency = invoiceDetail?.currency || 'AUD';
  const cards = providedCards ?? fetchedCards;
  const rawTransactions =
    providedTransactions ||
    invoiceDetail?.transactions ||
    invoiceDetail?.transaction_list ||
    [];

  const transactions = useMemo(
    () =>
      rawTransactions.map((transaction, index) => {
        let amountCents = 0;
        if (transaction.amount_cents != null) {
          amountCents = Number(transaction.amount_cents);
        } else if (transaction.total_cents != null) {
          amountCents = Number(transaction.total_cents);
        } else if (transaction.amount != null) {
          const parsed = parseFloat(transaction.amount);
          amountCents = Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
        }

        const txId =
          transaction.payment_transaction_id ||
          transaction.id ||
          transaction.transaction_id ||
          `${invoiceDetail?.id || invoiceDetail?.invoice_number}-${index}`;

        const attemptLabel = transaction.attempt
          ? `Attempt #${transaction.attempt}`
          : null;
        const description =
          transaction.description ||
          transaction.name ||
          transaction.type ||
          (attemptLabel
            ? `Payment (${attemptLabel})`
            : `Payment transaction #${txId}`);

        return {
          id: txId,
          date:
            transaction.created_at ||
            transaction.date ||
            transaction.transaction_date ||
            transaction.paid_at,
          description,
          reference: transaction.payment_transaction_id
            ? `TXN-${transaction.payment_transaction_id}`
            : transaction.reference || '—',
          status:
            transaction.status ||
            (transaction.succeeded === true
              ? 'succeeded'
              : transaction.failed === true
                ? 'failed'
                : 'completed'),
          amount_cents: amountCents,
          currency: transaction.currency || currency,
          errorMessage: transaction.error_message || null,
          method: transaction.method || transaction.payment_method || 'Card',
        };
      }),
    [
      invoiceDetail?.id,
      invoiceDetail?.invoice_number,
      rawTransactions,
      currency,
    ],
  );

  const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  const visibleTransactions = transactions.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const GetAPIcalls = useCallback(
    async (url, isBlob = false) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${baseUrl.replace(/\/$/, '')}${url}`;
      const headers = {};

      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));

        throw new Error(errorData.message || 'Unable to complete the request.');
      }

      return isBlob ? response.blob() : response.json();
    },
    [baseUrl, navigate],
  );

  /* ------------------------------------------------------------------ */
  /*  Self-fetch — GET /api/v1/agency/subscription/invoices/{id}         */
  /*  (path is the numeric PK) when the page is opened directly.         */
  /* ------------------------------------------------------------------ */
  const loadInvoiceById = useCallback(async () => {
    if (!routeInvoiceId) return;
    setLoadingInvoice(true);
    setFetchError('');
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/subscription/invoices/${routeInvoiceId}`,
      );
      if (response.status === 200) {
        setFetchedInvoice(response.data);
      } else {
        const message = response.message || 'Could not load invoice.';
        setFetchError(message);
        showToast?.(message, 'error');
      }
    } catch (error) {
      const message = error.message || 'Could not load invoice.';
      setFetchError(message);
      showToast?.(message, 'error');
    } finally {
      setLoadingInvoice(false);
    }
  }, [routeInvoiceId, GetAPIcalls, showToast]);

  useEffect(() => {
    if (providedInvoice || location.state?.invoice) return;
    if (!routeInvoiceId) return;
    loadInvoiceById();
  }, [
    providedInvoice,
    location.state?.invoice,
    routeInvoiceId,
    loadInvoiceById,
  ]);

  /* PDF fallback for direct opens — GET …/invoices/{id}/pdf as a blob
     (same flow as the list screen's downloadInvoicePdf). */
  const downloadPdfInternal = async () => {
    const id = invoiceDetail?.id;
    if (!id) return;
    try {
      const blob = await GetAPIcalls(
        `/api/v1/agency/subscription/invoices/${id}/pdf`,
        true,
      );
      if (!blob || blob.size === 0) {
        showToast?.('No PDF available for this invoice', 'error');
        return;
      }
      const pdfBlob =
        blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${invoiceDetail.invoice_number || `invoice-${id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showToast?.(error.message, 'error');
    }
  };

  const handleDownloadPdf = (id) => {
    if (onDownloadPdf) return onDownloadPdf(id);
    return downloadPdfInternal();
  };

  /* ------------------------------------------------------------------ */
  /*  POST /subscription/microform-session → { jwt, client_library }     */
  /* ------------------------------------------------------------------ */
  const postApi = useCallback(
    async (url) => {
      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const apiUrl = `${baseUrl.replace(/\/$/, '')}${url}`;
      try {
        const response = await fetch(apiUrl, { method: 'POST', headers });
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
            throw new Error('Session expired. Please log in again.');
          }
          const errorData = await response.json().catch(() => ({
            message: 'An unexpected error occurred',
          }));
          throw new Error(errorData.message || 'Something went wrong');
        }
        return await response.json();
      } catch (error) {
        throw new Error(
          error.message || 'Network error, please try again later',
        );
      }
    },
    [baseUrl, navigate],
  );

  /* ------------------------------------------------------------------ */
  /*  Microform setup — number + securityCode iframes inside our        */
  /*  styled containers; expiry stays a normal input (MM/YY) so nothing */
  /*  else in the modal layout changes.                                 */
  /* ------------------------------------------------------------------ */
  const initializeMicroform = useCallback((jwt) => {
    try {
      if (microformRef.current) return; // already mounted
      const flex = new window.Flex(jwt);
      const microform = flex.microform({
        styles: {
          input: {
            'font-size': '16px',
            'font-family': 'Work Sans, sans-serif',
            'letter-spacing': '0.5px',
          },
          ':focus': { color: '#0f172a' },
          '::placeholder': { color: '#b6c2d1', 'font-size': '16px' },
        },
      });
      const number = microform.createField('number', {
        placeholder: 'Card Number',
      });
      const securityCode = microform.createField('securityCode', {
        placeholder: 'CVV',
      });
      number.on('change', function (data) {
        setCardSuccess(data.valid);
        if (data.empty) setCardError('Card number is required');
        else if (!data.valid) setCardError('Invalid card number');
        else setCardError('');
      });
      securityCode.on('change', function (data) {
        setCvvSuccess(data.valid);
        if (data.empty) setCvvError('CVV is required');
        else if (!data.valid) setCvvError('Invalid CVV');
        else setCvvError('');
      });
      number.on('focus', () => setCardFocused(true));
      number.on('blur', () => setCardFocused(false));
      securityCode.on('focus', () => setCvvFocused(true));
      securityCode.on('blur', () => setCvvFocused(false));
      number.load('#card-number-container');
      securityCode.load('#card-cvv-container');
      microformRef.current = { microform, number, securityCode };
      setMicroformReady(true);
    } catch (error) {
      console.error('Microform initialization failed', error);
      setPaymentError(
        'Secure card fields could not be loaded. Please try again.',
      );
    }
  }, []);

  /* Unload the iframes + reset the microfield states */
  const destroyMicroform = useCallback(() => {
    try {
      microformRef.current?.number?.unload?.();
    } catch {
      /* already detached */
    }
    try {
      microformRef.current?.securityCode?.unload?.();
    } catch {
      /* already detached */
    }
    microformRef.current = null;
    setMicroformReady(false);
    setCardSuccess(false);
    setCvvSuccess(false);
    setCardError('');
    setCvvError('');
    setCardFocused(false);
    setCvvFocused(false);
  }, []);

  /* Fetch a fresh capture-context JWT + load the Flex client library */
  const ensureMicroformSession = useCallback(async () => {
    if (microformData?.jwt) return; // session already active for this open
    try {
      const response = await postApi(MICROFORM_SESSION_PATH);
      const { jwt, client_library } = response?.data || {};
      if (!jwt) throw new Error('Secure card session could not be started.');
      setMicroformData({ jwt });
      if (!window.Flex && client_library) {
        const script = document.createElement('script');
        script.src = client_library;
        script.async = true;
        script.onload = () => setFlexReady(true);
        script.onerror = () =>
          setPaymentError('Card security script failed to load.');
        document.body.appendChild(script);
      } else {
        setFlexReady(true);
      }
    } catch (error) {
      setPaymentError(
        error.message || 'Secure card fields could not be started.',
      );
      showToast?.(
        error.message || 'Secure card fields could not be started.',
        'error',
      );
    }
  }, [microformData, postApi, showToast]);

  /* Mount the microform fields once:
     modal open · manual tab active · session JWT ready · Flex loaded. */
  useEffect(() => {
    if (
      !paymentModalOpen ||
      paymentMethod !== 'manual' ||
      !microformData?.jwt ||
      !flexReady ||
      microformRef.current
    ) {
      return;
    }
    initializeMicroform(microformData.jwt);
  }, [
    paymentModalOpen,
    paymentMethod,
    microformData,
    flexReady,
    initializeMicroform,
  ]);

  /* Start the secure session whenever the manual tab becomes active. */
  useEffect(() => {
    if (paymentModalOpen && paymentMethod === 'manual') {
      ensureMicroformSession();
    }
  }, [paymentModalOpen, paymentMethod, ensureMicroformSession]);

  /* Full teardown when the modal closes. */
  useEffect(() => {
    if (!paymentModalOpen) {
      destroyMicroform();
      setMicroformData(null);
    }
  }, [paymentModalOpen, destroyMicroform]);

  /* Tokenize the card — the PAN/CVV never leave the Microform iframe;
     only the transient token goes into the payment payload. */
  const tokenizeCard = (expirationMonth, expirationYear) =>
    new Promise((resolve, reject) => {
      try {
        microformRef.current?.microform.createToken(
          { expirationMonth, expirationYear },
          (err, token) => {
            if (err || !token) {
              reject(
                new Error(
                  'We could not secure your card details. Please check the card and try again.',
                ),
              );
            } else {
              resolve(token);
            }
          },
        );
      } catch (e) {
        reject(e);
      }
    });

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!paymentModalOpen) return undefined;

    let cancelled = false;

    const applyCards = (nextCards) => {
      if (cancelled) return;
      setFetchedCards(nextCards);
      setSelectedCardId(defaultCardId(nextCards));
      setPaymentMethod(nextCards.length ? 'saved' : 'manual');
    };

    if (Array.isArray(providedCards)) {
      applyCards(providedCards);
      setCardsLoading(false);
      return undefined;
    }

    const loadCards = async () => {
      setCardsLoading(true);
      setCardsError('');

      try {
        const response = await GetAPIcalls(CARDS_PATH);

        if (response.status !== 200) {
          throw new Error(
            response.message || 'Unable to retrieve saved cards.',
          );
        }

        applyCards(response?.data?.cards || []);
      } catch (error) {
        if (!cancelled) {
          const message =
            error.message ||
            'Unable to retrieve saved cards. You can add a new card instead.';
          setCardsError(message);
          setFetchedCards([]);
          setPaymentMethod('manual');
          showToast?.(message, 'error');
        }
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    };

    loadCards();

    return () => {
      cancelled = true;
    };
  }, [GetAPIcalls, paymentModalOpen, providedCards, showToast]);

  const status = String(invoiceDetail?.status || 'pending').toLowerCase();
  const isPaid = status === 'paid' || status === 'settled';
  const hasPdf = invoiceDetail?.pdf_available !== false;

  /* GAP-11 line_items → display rows (description + quantity/unit-price
     caption + amount). Negative amounts (credit allowances) render in
     green, matching the transaction table's convention. */
  const lineItems = (invoiceDetail?.line_items || []).map((item, index) => {
    const qty = Number(item.quantity) || 0;
    let caption;
    if (item.unit_price_cents != null) {
      caption =
        qty > 1
          ? `${item.quantity} × ${formatCents(item.unit_price_cents, currency)}`
          : formatCents(item.unit_price_cents, currency);
    } else {
      caption = qty > 1 ? `Quantity ${item.quantity}` : 'Billed from usage';
    }
    return {
      key: item.code || `line-item-${index}`,
      description: item.description || 'Line item',
      caption,
      amount_cents: item.amount_cents ?? 0,
    };
  });

  const handlePay = () => {
    if (isPaid) return;

    setPaymentError('');
    setPaymentMethod('saved');
    setSelectedCardId('');
    setManualCard(emptyManualCard);
    destroyMicroform();
    setMicroformData(null);
    setPaymentModalOpen(true);
  };

  const switchMethod = (method) => {
    if (method === paymentMethod) return;
    if (method === 'saved') destroyMicroform(); // containers unmount
    setPaymentMethod(method);
    setPaymentError('');
  };

  const updateManualCard = (key, value) => {
    setManualCard((current) => ({ ...current, [key]: value }));
    setPaymentError('');
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    setPaymentError('');

    if (paymentMethod === 'saved' && !selectedCardId) {
      setPaymentError('Select a saved card to continue.');
      return;
    }

    let paymentPayload =
      paymentMethod === 'saved'
        ? { type: 'saved-card', cardId: selectedCardId }
        : null;

    if (paymentMethod === 'manual') {
      if (!microformReady || !microformRef.current) {
        setPaymentError('Secure card fields are still loading — one moment…');
        return;
      }

      if (!cardSuccess || !cvvSuccess) {
        if (!cardSuccess) setCardError((e) => e || 'Enter a valid card number');
        if (!cvvSuccess) setCvvError((e) => e || 'Enter a valid CVV');
        setPaymentError(
          'Enter a valid card number, expiry and CVV to continue.',
        );
        return;
      }

      const expiryParts = manualCard.expiry.split('/');
      const expiryMonth = expiryParts[0];
      const expiryYear = expiryParts[1] ? `20${expiryParts[1]}` : '';

      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(manualCard.expiry)) {
        setPaymentError('Enter the card expiry as MM/YY.');
        return;
      }

      setPaymentSubmitting(true);
      try {
        const transientToken = await tokenizeCard(expiryMonth, expiryYear);
        /* Token-only payload — the raw card number / CVV never reach
           our servers. Keep whatever extra keys your pay API needs. */
        paymentPayload = {
          type: 'new-card',
          transientToken,
          card: {
            expiryMonth,
            expiryYear,
          },
        };
      } catch (error) {
        setPaymentError(
          error.message ||
            'We could not secure your card details. Please try again.',
        );
        setPaymentSubmitting(false);
        return;
      }
    }

    setPaymentSubmitting(true);

    try {
      if (onPay) {
        await onPay(invoiceDetail, paymentPayload);
      } else {
        /* Pay integration — POST /api/v1/agency/subscription/invoices/{id}/pay
           Saved card → body { payment_method_id } (omit = default card)
           New card   → ?save_card=true + body { transient_token }
           (save then charge in one call — no separate POST /cards) */
        const payToken = localStorage.getItem('jwtToken');
        const payHeaders = { 'Content-Type': 'application/json' };
        if (payToken) payHeaders.Authorization = `Bearer ${payToken}`;
        const payUrl = `${baseUrl.replace(/\/$/, '')}/api/v1/agency/subscription/invoices/${invoiceDetail?.id}/pay${
          paymentMethod === 'manual' ? '?save_card=true' : ''
        }`;
        const payBody =
          paymentMethod === 'saved'
            ? paymentPayload?.cardId
              ? { payment_method_id: paymentPayload.cardId }
              : {}
            : { transient_token: paymentPayload?.transientToken };
        const response = await fetch(payUrl, {
          method: 'POST',
          headers: payHeaders,
          body: JSON.stringify(payBody),
        });
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
            throw new Error('Session expired. Please log in again.');
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData?.message ||
              'Payment could not be completed. Please try again.',
          );
        }
        const result = await response.json().catch(() => ({}));
        if (response.status === 200) {
          showToast?.('Payment successful', 'success');
          navigate(-1);
        }
      }
      setPaymentModalOpen(false);
    } catch (error) {
      setPaymentError(
        error.message || 'Payment could not be started. Please try again.',
      );
    } finally {
      setPaymentSubmitting(false);
    }
  };

  if (detailLoading || (loadingInvoice && !invoiceDetail)) {
    return (
      <div className="w-full animate-pulse px-4 pb-10 pt-4 sm:px-6 lg:px-8">
        <div className="h-5 w-24 rounded bg-slate-200" />
        <div className="mt-6 h-48 rounded-[28px] bg-slate-200" />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="h-28 rounded-2xl bg-slate-200" />
          ))}
        </div>
        <div className="mt-5 h-96 rounded-[24px] bg-slate-200" />
      </div>
    );
  }

  if (!invoiceDetail) {
    return (
      <div className="flex min-h-[55vh] flex-col items-center justify-center px-6 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#EEF2FF] text-[#375AE7]">
          <ReceiptText className="h-6 w-6" />
        </span>
        <h1 className="mt-5 text-xl font-semibold text-slate-900">
          Invoice details unavailable
        </h1>
        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
          {fetchError ||
            'We could not find the invoice details. Return to the invoice list and try again.'}
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex h-10 items-center gap-2 rounded-xl bg-[#375AE7] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4fd1]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to invoices
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-white rounded-2xl  px-4 pb-10 pt-4 text-slate-900 sm:px-6 lg:px-8 lg:min-w-[78vw] lg:max-w-[80vw]">
      <div className="mx-auto max-w-7xl">
        <section className="relative mt-5 overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-[0_24px_70px_-38px_rgba(15,23,42,0.4)]">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#375AE7] via-indigo-500 to-sky-400" />
          <div className="relative flex flex-col gap-6 p-5 sm:p-7 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#375AE7] to-indigo-600 text-white shadow-[0_14px_24px_-12px_rgba(55,90,231,0.7)]">
                <ReceiptText className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                    Invoice details
                  </p>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${statusClass(
                      status,
                      statusStyles,
                    )}`}
                  >
                    {invoiceDetail.status || 'Pending'}
                  </span>
                </div>
                <h1 className="mt-2 truncate text-2xl font-bold tracking-[-0.04em] text-slate-950 sm:text-3xl">
                  {invoiceDetail.invoice_number ||
                    `Invoice ${invoiceDetail.id}`}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-4 w-4 text-slate-400" />
                    {formatDate(invoiceDetail.period_start)} –{' '}
                    {formatDate(invoiceDetail.period_end)}
                  </span>
                  {invoiceDetail.agency_name ? (
                    <>
                      <span className="text-slate-300">•</span>
                      <span className="font-medium text-slate-600">
                        {invoiceDetail.agency_name}
                      </span>
                    </>
                  ) : null}
                  <span className="text-slate-300">•</span>
                  <span>{currency} billing</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 lg:justify-end">
              <button
                type="button"
                onClick={() => handleDownloadPdf(invoiceDetail.id)}
                disabled={!hasPdf}
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
              {!isPaid ? (
                <button
                  type="button"
                  onClick={handlePay}
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-[#375AE7] to-indigo-600 px-5 text-sm font-semibold text-white shadow-[0_12px_22px_-12px_rgba(55,90,231,0.8)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_28px_-12px_rgba(55,90,231,0.9)]"
                >
                  <CreditCard className="h-4 w-4" />
                  Pay now
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Total due"
            value={formatCents(invoiceDetail.total_cents, currency)}
            caption={isPaid ? 'Payment received' : 'Amount payable'}
            icon={WalletCards}
            tone="green"
          />
          <StatCard
            label="Subtotal"
            value={formatCents(invoiceDetail.subtotal_cents, currency)}
            caption="Before tax"
            icon={FileText}
            tone="blue"
          />
          <StatCard
            label="Volume"
            value={invoiceDetail.volume ?? '—'}
            caption={'Usage volume'}
            icon={ReceiptText}
            tone="slate"
          />
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <section className="min-w-0 overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_18px_50px_-38px_rgba(15,23,42,0.45)]">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                  Activity
                </p>
                <h2 className="mt-1 text-lg font-bold tracking-[-0.03em] text-slate-900">
                  Transaction history
                </h2>
              </div>
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500">
                <Clock3 className="h-3.5 w-3.5" />
                {transactions.length} transaction
                {transactions.length === 1 ? '' : 's'}
              </span>
            </div>

            {transactions.length === 0 ? (
              <EmptyTransactions />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] border-collapse text-left">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/80 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                        <th className="px-6 py-3.5">Transaction</th>
                        <th className="px-4 py-3.5">Date</th>
                        <th className="px-4 py-3.5">Status</th>
                        <th className="px-6 py-3.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleTransactions.map((transaction) => {
                        const transactionStatus = String(
                          transaction.status || 'completed',
                        ).toLowerCase();
                        return (
                          <tr
                            key={transaction.id}
                            className="border-b border-slate-100 last:border-0 transition hover:bg-[#F8FAFF]"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#EEF2FF] text-[#375AE7]">
                                  <CreditCard className="h-4 w-4" />
                                </span>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-800">
                                    {transaction.description}
                                  </p>
                                  {transaction.reference &&
                                  transaction.reference !== '—' ? (
                                    <p className="text-xs text-slate-400">
                                      Ref: {transaction.reference}
                                    </p>
                                  ) : null}
                                  {transaction.errorMessage ? (
                                    <p className="mt-0.5 text-xs text-red-500">
                                      {transaction.errorMessage}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-600">
                              {formatDate(transaction.date)}
                            </td>

                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${statusClass(
                                  transactionStatus,
                                  transactionStatusStyles,
                                )}`}
                              >
                                {transaction.status || 'Completed'}
                              </span>
                            </td>
                            <td
                              className={`px-6 py-4 text-right text-sm font-bold ${
                                Number(transaction.amount_cents) < 0
                                  ? 'text-emerald-600'
                                  : 'text-slate-900'
                              }`}
                            >
                              {formatCents(
                                transaction.amount_cents,
                                transaction.currency || currency,
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <p className="text-xs text-slate-500">
                    Showing{' '}
                    <span className="font-semibold text-slate-700">
                      {(page - 1) * PAGE_SIZE + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-semibold text-slate-700">
                      {Math.min(page * PAGE_SIZE, transactions.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-semibold text-slate-700">
                      {transactions.length}
                    </span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      aria-label="Previous page"
                      disabled={page === 1}
                      onClick={() =>
                        setPage((current) => Math.max(1, current - 1))
                      }
                      className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-35"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                      .slice(0, 5)
                      .map((pageNumber) => (
                        <button
                          type="button"
                          key={pageNumber}
                          onClick={() => setPage(pageNumber)}
                          className={`grid h-9 min-w-9 place-items-center rounded-xl px-2 text-xs font-bold transition ${
                            page === pageNumber
                              ? 'bg-[#375AE7] text-white shadow-sm'
                              : 'border border-slate-200 bg-white text-slate-500 hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7]'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    <button
                      type="button"
                      aria-label="Next page"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((current) => Math.min(totalPages, current + 1))
                      }
                      className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-35"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>

          <aside className="space-y-5">
            <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_18px_50px_-38px_rgba(15,23,42,0.45)]">
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#EEF2FF] text-[#375AE7]">
                  <CalendarDays className="h-4 w-4" />
                </span>
                <h2 className="text-sm font-bold text-slate-900">
                  Invoice timeline
                </h2>
              </div>
              <div className="mt-5 space-y-5">
                <div className="relative flex gap-3">
                  <span className="relative z-10 mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">
                      Issued
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {formatDate(invoiceDetail.issued_at)}
                    </p>
                  </div>
                </div>
                <div className="relative flex gap-3">
                  <span className="relative z-10 mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#EEF2FF] text-[#375AE7]">
                    <Clock3 className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">
                      Payment status
                    </p>
                    <p className="mt-0.5 text-xs capitalize text-slate-500">
                      {invoiceDetail.paid_at
                        ? `Paid ${formatDate(invoiceDetail.paid_at)}`
                        : invoiceDetail.payment_status || 'Pending'}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {lineItems.length > 0 ? (
              <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_18px_50px_-38px_rgba(15,23,42,0.45)]">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#EEF2FF] text-[#375AE7]">
                    <ReceiptText className="h-4 w-4" />
                  </span>
                  <h2 className="text-sm font-bold text-slate-900">
                    Payment Details
                  </h2>
                </div>
                <div className="mt-5 space-y-4">
                  {lineItems.map((item) => (
                    <div
                      key={item.key}
                      className="flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-800">
                          {item.description}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {item.caption}
                        </p>
                      </div>
                      <p
                        className={`shrink-0 text-sm font-bold ${
                          Number(item.amount_cents) < 0
                            ? 'text-emerald-600'
                            : 'text-slate-900'
                        }`}
                      >
                        {formatCents(item.amount_cents, currency)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 space-y-2.5 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-500">
                      Subtotal (excl. tax)
                    </span>
                    <span className="font-semibold text-slate-800">
                      {formatCents(invoiceDetail.subtotal_cents, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-500">
                        {invoiceDetail.tax_label || 'GST'}
                      </span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                        {invoiceDetail.gst_rate_bps != null
                          ? `${invoiceDetail.gst_rate_bps / 100}%`
                          : '10%'}
                      </span>
                    </div>
                    <span className="font-semibold text-slate-800">
                      +{formatCents(invoiceDetail.tax_amount_cents, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 text-xs">
                    <div>
                      <span className="font-bold text-slate-900">Total</span>
                      <span className="ml-1 text-[11px] text-slate-400">
                        (incl. {invoiceDetail.tax_label || 'GST'})
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-950">
                      {formatCents(invoiceDetail.total_cents, currency)}
                    </span>
                  </div>
                </div>
              </section>
            ) : null}

            <section className="overflow-hidden rounded-[24px] bg-gradient-to-br from-[#172554] via-[#243b8f] to-[#375AE7] p-5 text-white shadow-[0_22px_42px_-24px_rgba(37,64,170,0.8)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-blue-100/75">
                    Payment summary
                  </p>
                  <p className="mt-3 text-3xl font-bold tracking-[-0.05em]">
                    {formatCents(invoiceDetail.total_cents, currency)}
                  </p>
                </div>
                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-white/15">
                  <CreditCard className="h-5 w-5" />
                </span>
              </div>
              <div className="mt-5 border-t border-white/15 pt-4 text-xs text-blue-100/75">
                {isPaid
                  ? 'This invoice has been paid.'
                  : 'Complete payment securely to settle this invoice.'}
              </div>
              {!isPaid ? (
                <button
                  type="button"
                  onClick={handlePay}
                  className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-bold text-[#375AE7] transition hover:bg-blue-50"
                >
                  <CreditCard className="h-4 w-4" />
                  Pay this invoice
                </button>
              ) : null}
            </section>
          </aside>
        </div>
      </div>

      {paymentModalOpen ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
          role="presentation"
          onMouseDown={() => {
            if (!paymentSubmitting) setPaymentModalOpen(false);
          }}
        >
          <form
            onSubmit={submitPayment}
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
            className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_30px_100px_-30px_rgba(15,23,42,0.65)]"
          >
            <div className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-[#EEF2FF] via-white to-sky-50 px-5 py-5 sm:px-7">
              <div className="absolute -right-12 -top-16 h-40 w-40 rounded-full bg-[#375AE7]/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#375AE7] to-indigo-600 text-white shadow-[0_12px_22px_-12px_rgba(55,90,231,0.8)]">
                    <CreditCard className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                      Secure payment
                    </p>
                    <h2
                      id="payment-modal-title"
                      className="mt-1 text-xl font-bold tracking-[-0.04em] text-slate-950"
                    >
                      Choose a payment method
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">
                      Pay {formatCents(invoiceDetail.total_cents, currency)} for{' '}
                      {invoiceDetail.invoice_number || 'this invoice'}.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPaymentModalOpen(false)}
                  disabled={paymentSubmitting}
                  aria-label="Close payment dialog"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white/80 text-slate-500 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-7">
              {cardsError ? (
                <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-5 text-amber-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>{cardsError}</span>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => switchMethod('saved')}
                  className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                    paymentMethod === 'saved'
                      ? 'bg-white text-[#375AE7] shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Saved cards
                </button>
                <button
                  type="button"
                  onClick={() => switchMethod('manual')}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                    paymentMethod === 'manual'
                      ? 'bg-white text-[#375AE7] shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  Add new card
                </button>
              </div>

              {paymentMethod === 'saved' ? (
                <div className="mt-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Your cards
                      </h3>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Select a card to pay securely.
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
                      <LockKeyhole className="h-3.5 w-3.5" />
                      Secure checkout
                    </span>
                  </div>

                  {cardsLoading ? (
                    <div className="space-y-2.5">
                      {[1, 2].map((item) => (
                        <div
                          key={item}
                          className="h-[76px] animate-pulse rounded-2xl bg-slate-100"
                        />
                      ))}
                    </div>
                  ) : cards.length ? (
                    <div
                      className={`space-y-2.5 ${
                        cards.length > 10
                          ? 'max-h-[360px] overflow-y-auto pr-1'
                          : ''
                      }`}
                    >
                      {cards.map((card) => {
                        const selected = selectedCardId === card.id;
                        return (
                          <button
                            type="button"
                            key={card.id}
                            onClick={() => {
                              setSelectedCardId(card.id);
                              setPaymentError('');
                            }}
                            className={`flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition ${
                              selected
                                ? 'border-[#375AE7] bg-[#F5F7FF] shadow-[0_10px_24px_-18px_rgba(55,90,231,0.9)] ring-4 ring-[#375AE7]/10'
                                : 'border-slate-200 bg-white hover:border-[#375AE7]/35 hover:bg-[#FAFBFF]'
                            }`}
                          >
                            <span
                              className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                                selected
                                  ? 'bg-[#375AE7] text-white'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              <CreditCard className="h-5 w-5" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-bold text-slate-900">
                                  {cardBrandLabel(card.card_brand)} ••••{' '}
                                  {card.card_last4}
                                </span>
                                {card.is_default ? (
                                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                                    Default
                                  </span>
                                ) : null}
                              </span>
                              <span className="mt-1 block text-xs text-slate-500">
                                Expires{' '}
                                {String(card.card_expiry_month).padStart(
                                  2,
                                  '0',
                                )}
                                /{card.card_expiry_year}
                              </span>
                            </span>
                            <span
                              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                                selected
                                  ? 'border-[#375AE7]'
                                  : 'border-slate-300'
                              }`}
                            >
                              {selected ? (
                                <span className="h-2.5 w-2.5 rounded-full bg-[#375AE7]" />
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                      <CreditCard className="mx-auto h-7 w-7 text-slate-400" />
                      <p className="mt-2 text-sm font-semibold text-slate-700">
                        No saved cards found
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Add a new card to continue with this payment.
                      </p>
                      <button
                        type="button"
                        onClick={() => switchMethod('manual')}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#375AE7] px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-[#2f4fd1]"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add a card
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 sm:p-5">
                  <div className="mb-5 flex items-start gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#EEF2FF] text-[#375AE7]">
                      <CreditCard className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        Add card details
                      </p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Card details are encrypted by Microform and never touch
                        our servers.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-4">
                    <PaymentField
                      id="manual-card-number"
                      label="Card number"
                      icon={CreditCard}
                      className="sm:col-span-4"
                    >
                      {/* Microform number iframe mounts here */}
                      <div
                        id="card-number-container"
                        aria-label="Card number"
                        className={microFieldClass(
                          cardFocused,
                          !!cardError,
                          'pl-10',
                        )}
                      />
                      {cardError ? (
                        <p className="mt-1.5 text-[11px] font-medium text-red-500">
                          {cardError}
                        </p>
                      ) : null}
                    </PaymentField>

                    <PaymentField
                      id="manual-card-expiry"
                      label="Expiry"
                      icon={CalendarDays}
                      className="sm:col-span-2"
                    >
                      <input
                        id="manual-card-expiry"
                        value={manualCard.expiry}
                        onChange={(event) =>
                          updateManualCard(
                            'expiry',
                            formatExpiry(event.target.value),
                          )
                        }
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        maxLength={5}
                        placeholder="MM/YY"
                        className={`${paymentInputClass} pl-10`}
                      />
                    </PaymentField>

                    <PaymentField
                      id="manual-card-cvv"
                      label="CVV"
                      icon={LockKeyhole}
                      className="sm:col-span-2"
                    >
                      {/* Microform securityCode iframe mounts here */}
                      <div
                        id="card-cvv-container"
                        aria-label="CVV"
                        className={microFieldClass(
                          cvvFocused,
                          !!cvvError,
                          'pl-10',
                        )}
                      />
                      {cvvError ? (
                        <p className="mt-1.5 text-[11px] font-medium text-red-500">
                          {cvvError}
                        </p>
                      ) : null}
                    </PaymentField>
                  </div>

                  {!microformReady ? (
                    <div className="mt-4 space-y-2.5 rounded-xl border border-slate-200/80 bg-white/70 p-3">
                      <div className="h-3 w-40 animate-pulse rounded bg-slate-200" />
                      <p className="text-[11px] leading-4 text-slate-400">
                        Loading secure card fields…
                      </p>
                    </div>
                  ) : null}
                </div>
              )}

              {paymentError ? (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-5 text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{paymentError}</span>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <LockKeyhole className="h-4 w-4 text-emerald-600" />
                Secure payment · {currency}
              </div>
              <div className="flex gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={() => setPaymentModalOpen(false)}
                  disabled={paymentSubmitting}
                  className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    paymentSubmitting ||
                    cardsLoading ||
                    (paymentMethod === 'manual' && !microformReady)
                  }
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#375AE7] to-indigo-600 px-5 text-sm font-bold text-white shadow-[0_12px_22px_-12px_rgba(55,90,231,0.8)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0"
                >
                  {paymentSubmitting ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  {paymentSubmitting
                    ? 'Starting payment…'
                    : 'Continue to payment'}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
